import DataGridFooter from '@/components/common/DataGridFooter';
import Export from '@/components/common/Export';
import FadeSlide from '@/components/common/FadeSlide';
import SimpleSearchField from '@/components/common/SimpleSearchField';
import { AppLog, EnvInfo } from '@/types/analytics';
import { StripedDataGrid } from '@/util/datagrid';
import { getHighestNumber } from '@/util/number';
import { Button, CircularProgress, Stack } from '@mui/material';
import { GridRowClassNameParams, GridRowParams, GridSortModel, GridValidRowModel } from '@mui/x-data-grid-pro';
import { FC, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSessionStorage } from 'usehooks-ts';
import { appColumns } from '../../columns';
import AppLogInfo from './AppLogInfo';

type AppLogsProps = {
  name: string;
  uuid: string;
  appLogs: AppLog[];
  envInfo?: EnvInfo;
  selected: boolean;
  isLoading: boolean;
};

const AppLogs: FC<AppLogsProps> = ({ name, uuid, appLogs, envInfo, selected, isLoading }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState<string>('');
  const [showAll, setShowAll] = useState<boolean>(false);
  const [selectedLog, setSelectedLog] = useState<AppLog>();
  const [sortModal, setSortModal] = useSessionStorage<GridSortModel>('AppLogsSort', []);
  const [copySuccessful, setCopySuccessful] = useState<boolean>(false);
  const [openExport, setOpenExport] = useState<boolean>(false);

  const copyApplication = () => {
    let value = `Applog for ${uuid} ${envInfo?.session} ${envInfo?.appname} ${envInfo?.hostname}`;
    const largestId = getHighestNumber(appLogs, 'blockid').toString().length;

    appLogs.forEach((log) => {
      value += `\n${log.timestamp} ${log.blockid.padStart(largestId, ' ')} ${log.message}`;
    });
    navigator.clipboard.writeText(value);
  };

  const handleRowClick = (row: AppLog) => {
    setSelectedLog(row);

    searchParams.set('rowid', row.step);
    setSearchParams(searchParams);
  };

  const handleCloseDialog = () => {
    setSelectedLog(undefined);

    searchParams.delete('rowid');
    setSearchParams(searchParams);
  };

  const filteredLogs = appLogs.filter((log) =>
    Object.values(log).some((value) => value.toLowerCase().includes(search.trim().toLowerCase()))
  );

  useEffect(() => {
    const id = searchParams.get('rowid');
    if (!id) return;

    const foundId = appLogs.find((item) => item.step === id);
    if (!foundId) return;

    setSelectedLog(foundId);
  }, [appLogs]);

  return (
    <Stack height={'90%'}>
      <Stack direction='row' alignItems='center' justifyContent='space-between'>
        <SimpleSearchField search={search} setSearch={(newSearch) => setSearch(newSearch)} sx={{ width: '50%' }} />
        {filteredLogs.length > 0 && (
          <Button variant='contained' onClick={() => setOpenExport(true)} sx={{ mx: 2 }}>
            Export
          </Button>
        )}
      </Stack>
      {isLoading ? (
        <Stack alignItems='center' mt={10}>
          <CircularProgress color='inherit' />
        </Stack>
      ) : (
        selected && (
          <StripedDataGrid
            columns={appColumns()}
            rows={filteredLogs}
            getRowId={(row: GridValidRowModel) => row.step}
            getRowHeight={() => (showAll ? 'auto' : undefined)}
            localeText={{ noRowsLabel: 'No reports available' }}
            sortModel={sortModal}
            onSortModelChange={(newSort: GridSortModel) => setSortModal(newSort)}
            isRowSelectable={() => false}
            onRowDoubleClick={(value: GridRowParams<AppLog>) => handleRowClick(value.row)}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            getRowClassName={(params: GridRowClassNameParams<GridValidRowModel>) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
            }
            slots={{
              footer: () => (
                <DataGridFooter
                  length={filteredLogs.length}
                  checked={showAll}
                  onChange={(checked) => setShowAll(checked)}
                  copyApp={() => copyApplication()}
                />
              )
            }}
            sx={{ '& .MuiDataGrid-cell:focus': { outline: 'none' } }}
          />
        )
      )}
      {selectedLog && (
        <AppLogInfo log={selectedLog} copySuccessful={() => setCopySuccessful(true)} onClose={handleCloseDialog} />
      )}
      {copySuccessful && (
        <Stack
          position='absolute'
          top={0}
          width='100%'
          height='100%'
          justifyContent='start'
          alignItems='center'
          sx={{ pointerEvents: 'none' }}
        >
          <FadeSlide message={`Copied value to clipboard!`} onDone={() => setCopySuccessful(false)} />
        </Stack>
      )}
      <Export open={openExport} onClose={() => setOpenExport(false)} name={name} data={filteredLogs} />
    </Stack>
  );
};

export default AppLogs;
